import React, { useRef, useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheckCircle,
  faExclamationCircle,
  faInfoCircle,
  faTimesCircle,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import moment from "moment-timezone";
import { v4 as uuidv4 } from "uuid";

const DOUBLE_INPUT_INTERVAL = 10;

export const InputBIB = () => {
  const staticEventId = "verticalchallenge";
  const [rounds, setRounds] = useState([]);
  const [selectedRoundId, setSelectedRoundId] = useState("");
  const [bibNumber, setBibNumber] = useState("");
  const [message, setMessage] = useState(null);
  const [inputCount, setInputCount] = useState({});
  const [pendingBibs, setPendingBibs] = useState([]);
  const [history, setHistory] = useState([]);
  const [selectedEventId] = useState(staticEventId);
  const [clearHistoryWarning, setClearHistoryWarning] = useState(false);
  const [clearHistoryTimeout, setClearHistoryTimeout] = useState(null);

  const historyEndRef = useRef(null);

  const saveToLocalStorage = (
    currentHistory,
    newSelectedRound,
    currentPendingBibs
  ) => {
    try {
      const historyToSave = currentHistory.map(({ icon, ...rest }) => ({
        ...rest,
        icon:
          rest.status === "Berhasil disimpan"
            ? "check-circle"
            : rest.status === "Tertunda"
            ? "exclamation-circle"
            : rest.status.includes("Error")
            ? "times-circle"
            : rest.status === "Input ganda terdeteksi" ||
              rest.status.includes("Sudah direkam sebelumnya")
            ? "info-circle"
            : "unknown",
      }));
      const messageText =
        typeof message === "string" ? message : message?.props?.children || "";
      localStorage.setItem("bibNumber", bibNumber);
      localStorage.setItem("message", messageText);
      localStorage.setItem("inputCount", JSON.stringify(inputCount));
      localStorage.setItem("pendingBibs", JSON.stringify(currentPendingBibs));
      localStorage.setItem("history", JSON.stringify(historyToSave));
      localStorage.setItem(
        "selectedRound",
        newSelectedRound || selectedRoundId
      );
    } catch (error) {
      setMessage(
        <span>
          ERROR: LOCALSTORAGE FULL. PLEASE SYNC DATA OR CLEAR HISTORY TO
          CONTINUE.
        </span>
      );
    }
  };

  useEffect(() => {
    const initializeData = async () => {
      try {
        const savedBibNumber = localStorage.getItem("bibNumber") || "";
        const savedMessage = localStorage.getItem("message") || "";
        const savedInputCount =
          JSON.parse(localStorage.getItem("inputCount")) || {};
        const savedPendingBibs =
          JSON.parse(localStorage.getItem("pendingBibs")) || [];
        const savedRound = localStorage.getItem("selectedRound") || "";

        let savedHistory = [];
        const rawHistory = localStorage.getItem("history");
        if (rawHistory) {
          try {
            savedHistory = JSON.parse(rawHistory);
            if (!Array.isArray(savedHistory)) {
              savedHistory = [];
            } else {
              const latestEntries = {};
              savedHistory.forEach((entry) => {
                const key = `${entry.bib}-${entry.roundId}-${entry.pendingId}`;
                if (
                  !latestEntries[key] ||
                  new Date(entry.time) > new Date(latestEntries[key].time)
                ) {
                  latestEntries[key] = {
                    ...entry,
                    icon:
                      entry.status === "Berhasil disimpan"
                        ? "check-circle"
                        : entry.status === "Tertunda"
                        ? "exclamation-circle"
                        : entry.status.includes("Error")
                        ? "times-circle"
                        : entry.status === "Input ganda terdeteksi" ||
                          entry.status.includes("Sudah direkam sebelumnya")
                        ? "info-circle"
                        : "unknown",
                  };
                }
              });
              savedHistory = Object.values(latestEntries);
            }
          } catch (parseError) {
            savedHistory = [];
          }
        }

        setBibNumber(savedBibNumber);
        let messageContent = savedMessage;
        try {
          const parsedMessage = JSON.parse(savedMessage);
          messageContent = parsedMessage?.props?.children || savedMessage;
        } catch (e) {
          messageContent = savedMessage;
        }
        setMessage(messageContent ? <span>{messageContent}</span> : "");
        setInputCount(savedInputCount);
        setPendingBibs(savedPendingBibs);
        setHistory(savedHistory);
        setSelectedRoundId(savedRound);

        if (!navigator.onLine) {
          const cachedRounds = JSON.parse(localStorage.getItem("rounds")) || {};
          if (cachedRounds[selectedEventId]) {
            setRounds(cachedRounds[selectedEventId]);
            if (cachedRounds[selectedEventId].length > 0 && !savedRound) {
              const firstRound = cachedRounds[selectedEventId][0].id;
              setSelectedRoundId(firstRound);
              localStorage.setItem("selectedRound", firstRound);
            }
          } else {
            setMessage(
              <span>
                No cached rounds available. Please connect to load event data.
              </span>
            );
          }
          return;
        }

        try {
          const response = await fetch(
            `${import.meta.env.VITE_API_BASE_URL}/api/timing/events`,
            {
              method: "GET",
              headers: { "Content-Type": "application/json" },
            }
          );
          const text = await response.text();
          if (response.ok) {
            let data;
            try {
              data = JSON.parse(text);
            } catch (parseError) {
              setMessage(
                <span>
                  Failed to parse event data. Using cached data if available.
                </span>
              );
              const cachedRounds =
                JSON.parse(localStorage.getItem("rounds")) || {};
              if (cachedRounds[selectedEventId]) {
                setRounds(cachedRounds[selectedEventId]);
                if (cachedRounds[selectedEventId].length > 0 && !savedRound) {
                  const firstRound = cachedRounds[selectedEventId][0].id;
                  setSelectedRoundId(firstRound);
                  localStorage.setItem("selectedRound", firstRound);
                }
              }
              return;
            }
            const eventData = data.find(
              (event) => event.eventId === selectedEventId
            );
            if (
              eventData &&
              eventData.rounds &&
              Array.isArray(eventData.rounds)
            ) {
              const allRounds = eventData.rounds.filter(
                (round) => round.id && round.name
              );
              setRounds(allRounds.sort((a, b) => a.id.localeCompare(b.id)));
              if (allRounds.length > 0 && !savedRound) {
                const firstRound = allRounds[0].id;
                setSelectedRoundId(firstRound);
                localStorage.setItem("selectedRound", firstRound);
              } else if (allRounds.length === 0) {
                setSelectedRoundId("");
                localStorage.setItem("selectedRound", "");
                setMessage(<span>No rounds found for this event.</span>);
              }
            } else {
              setMessage(<span>No rounds found for this event.</span>);
              setRounds([]);
              setSelectedRoundId("");
            }
          } else {
            setMessage(
              <span>
                Failed to fetch rounds. Using cached data if available.
              </span>
            );
            const cachedRounds =
              JSON.parse(localStorage.getItem("rounds")) || {};
            if (cachedRounds[selectedEventId]) {
              setRounds(cachedRounds[selectedEventId]);
              if (cachedRounds[selectedEventId].length > 0 && !savedRound) {
                const firstRound = cachedRounds[selectedEventId][0].id;
                setSelectedRoundId(firstRound);
                localStorage.setItem("selectedRound", firstRound);
              } else {
                setSelectedRoundId("");
                localStorage.setItem("selectedRound", "");
              }
            } else {
              setSelectedRoundId("");
            }
          }
        } catch (error) {
          setMessage(
            <span>Error fetching rounds. Using cached data if available.</span>
          );
          const cachedRounds = JSON.parse(localStorage.getItem("rounds")) || {};
          if (cachedRounds[selectedEventId]) {
            setRounds(cachedRounds[selectedEventId]);
            if (cachedRounds[selectedEventId].length > 0 && !savedRound) {
              const firstRound = cachedRounds[selectedEventId][0].id;
              setSelectedRoundId(firstRound);
              localStorage.setItem("selectedRound", firstRound);
            } else {
              setSelectedRoundId("");
              localStorage.setItem("selectedRound", "");
            }
          } else {
            setSelectedRoundId("");
          }
        }
      } catch (error) {
        setMessage(
          <span>
            Error loading saved data. Please clear history and try again.
          </span>
        );
        setHistory([]);
        setPendingBibs([]);
        setInputCount({});
        setSelectedRoundId("");
      }
    };

    initializeData();
  }, [selectedEventId]);

  useEffect(() => {
    if (rounds.length > 0) {
      try {
        const cachedRounds = JSON.parse(localStorage.getItem("rounds")) || {};
        cachedRounds[selectedEventId] = rounds;
        localStorage.setItem("rounds", JSON.stringify(cachedRounds));
      } catch (error) {
        setMessage(
          <span>
            ERROR: LOCALSTORAGE FULL. PLEASE SYNC DATA OR CLEAR HISTORY TO
            CONTINUE.
          </span>
        );
      }
    }
  }, [rounds, selectedEventId]);

  useEffect(() => {
    if (historyEndRef.current) {
      historyEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [history]);

  useEffect(() => {
    const handleOnline = async () => {
      if (pendingBibs.length > 0) {
        setMessage(<span>Back online! Syncing pending data...</span>);
        await handleSyncClick();
      }
    };
    window.addEventListener("online", handleOnline);
    return () => window.removeEventListener("online", handleOnline);
  }, [pendingBibs]);

  const handleNumpadClick = (value) => {
    if (clearHistoryWarning) {
      setClearHistoryWarning(false);
    }
    if (bibNumber.length < 10) {
      setBibNumber((prev) => prev + value);
    } else {
      setMessage(<span>Maximum BIB length reached.</span>);
    }
  };

  const handleDelete = () => {
    if (bibNumber.length > 0) {
      setBibNumber((prev) => prev.slice(0, -1));
      setMessage("");
    } else {
      setMessage(<span>No BIB number to delete.</span>);
    }
    saveToLocalStorage(history, selectedRoundId, pendingBibs);
  };

  const getNextRoundId = async (bib) => {
    // Only check rounds when online
    if (!navigator.onLine) {
      console.log(
        `[DEBUG] getNextRoundId: Device offline, cannot check rounds for BIB ${bib}`
      );
      return null;
    }

    let serverCompletedRounds = [];
    try {
      const response = await fetch(
        `${
          import.meta.env.VITE_API_BASE_URL
        }/api/timing/check-participant?eventId=${encodeURIComponent(
          selectedEventId
        )}&bib=${encodeURIComponent(bib)}`,
        {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        }
      );
      if (response.ok) {
        const data = await response.json();
        if (data.exists && data.completedRounds) {
          serverCompletedRounds = data.completedRounds;
        }
      }
    } catch (error) {
      console.error(`[ERROR] getNextRoundId: Failed to check participant`, {
        bib,
        error: error.message,
      });
    }

    const sortedRounds = rounds.sort((a, b) => a.id.localeCompare(b.id));
    for (let i = 0; i < sortedRounds.length; i++) {
      if (!serverCompletedRounds.includes(sortedRounds[i].id)) {
        return sortedRounds[i].id;
      }
    }

    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!bibNumber) {
      setMessage(<span>Please enter a BIB number.</span>);
      saveToLocalStorage(history, selectedRoundId, pendingBibs);
      return;
    }

    if (!selectedRoundId) {
      setMessage(<span>Please select a round.</span>);
      saveToLocalStorage(history, selectedRoundId, pendingBibs);
      return;
    }

    const selectedRoundData = rounds.find(
      (round) => round.id === selectedRoundId
    );
    if (!selectedRoundData) {
      setMessage(<span>Invalid round selected.</span>);
      saveToLocalStorage(history, selectedRoundId, pendingBibs);
      return;
    }

    const currentTime = moment().tz("Asia/Jakarta").toISOString();
    const startTime = moment(selectedRoundData.startTime);
    const currentMoment = moment(currentTime);

    if (currentMoment.isBefore(startTime)) {
      setMessage(
        <span>
          BIB {bibNumber} in {selectedRoundData.name} cannot be recorded! Start
          time has not been reached.
        </span>
      );
      setBibNumber("");
      saveToLocalStorage(history, selectedRoundId, pendingBibs);
      return;
    }

    const validatedInterval = Number.isFinite(Number(DOUBLE_INPUT_INTERVAL))
      ? Number(DOUBLE_INPUT_INTERVAL)
      : 0;

    if (validatedInterval > 0) {
      const recentEntry = history
        .filter(
          (entry) =>
            entry.bib === bibNumber &&
            entry.eventId === selectedEventId &&
            ["Berhasil disimpan", "Tertunda"].includes(entry.status)
        )
        .sort((a, b) => new Date(b.time) - new Date(a.time))[0];
      if (recentEntry) {
        const entryTime = new Date(recentEntry.time);
        const currentTimeDate = new Date(currentTime);
        const timeDifference = (currentTimeDate - entryTime) / (1000 * 60);
        if (timeDifference < validatedInterval) {
          const newHistory = [
            ...history,
            {
              bib: bibNumber,
              roundId: selectedRoundId,
              location: selectedRoundData.name,
              eventId: selectedEventId,
              time: currentTime,
              status: "Input ganda terdeteksi",
              icon: "info-circle",
              pendingId: uuidv4(),
            },
          ];
          setHistory(newHistory);
          setMessage(
            <span>
              Duplicate BIB {bibNumber} within {validatedInterval} minutes!
              Previous submission at{" "}
              {moment(recentEntry.time).format("HH:mm:ss")} in{" "}
              {recentEntry.location}.
            </span>
          );
          setBibNumber("");
          saveToLocalStorage(newHistory, selectedRoundId, pendingBibs);
          return;
        }
      }
    }

    let roundIdToUse = selectedRoundId;
    if (navigator.onLine) {
      const nextRoundId = await getNextRoundId(bibNumber);
      if (nextRoundId) {
        roundIdToUse = nextRoundId;
      } else {
        setMessage(
          <span>
            No rounds available for BIB {bibNumber}. All rounds completed or BIB
            not found.
          </span>
        );
        saveToLocalStorage(history, selectedRoundId, pendingBibs);
        return;
      }
    }

    const roundDataToUse = rounds.find((round) => round.id === roundIdToUse);
    if (!roundDataToUse) {
      setMessage(<span>Invalid round selected.</span>);
      saveToLocalStorage(history, selectedRoundId, pendingBibs);
      return;
    }

    const pendingId = uuidv4();
    const filteredHistory = history.filter(
      (entry) =>
        !(
          entry.bib === bibNumber &&
          entry.roundId === roundIdToUse &&
          entry.status === "Tertunda"
        )
    );
    const filteredPendingBibs = pendingBibs.filter(
      (entry) =>
        !(
          entry.bib === bibNumber &&
          entry.roundId === roundIdToUse &&
          entry.status === "Tertunda"
        )
    );
    const newHistory = [
      ...filteredHistory,
      {
        bib: bibNumber,
        roundId: roundIdToUse,
        location: roundDataToUse.name || roundIdToUse,
        eventId: selectedEventId,
        time: currentTime,
        status: "Tertunda",
        icon: "exclamation-circle",
        pendingId,
      },
    ];
    const newPendingBibs = [
      ...filteredPendingBibs,
      {
        bib: bibNumber,
        roundId: roundIdToUse,
        eventId: selectedEventId,
        time: currentTime,
        status: "Tertunda",
        pendingId,
      },
    ];
    setPendingBibs(newPendingBibs);
    setHistory(newHistory);
    setMessage(<span>Data saved locally for BIB {bibNumber}.</span>);
    setInputCount((prev) => ({
      ...prev,
      [bibNumber]: (prev[bibNumber] || 0) + 1,
    }));
    setBibNumber("");
    setSelectedRoundId(roundIdToUse);
    saveToLocalStorage(newHistory, roundIdToUse, newPendingBibs);

    // Automatically attempt to sync if online
    if (navigator.onLine) {
      await handleOnlineSubmit(
        bibNumber,
        selectedEventId,
        roundIdToUse,
        currentTime,
        pendingId
      );
    }
  };

  const handleOnlineSubmit = async (bib, eventId, roundId, time, pendingId) => {
    console.log(
      `[DEBUG] handleOnlineSubmit: Attempting to sync BIB ${bib} for round ${roundId}`,
      {
        bib,
        eventId,
        roundId,
        time,
        pendingId,
      }
    );

    try {
      // Check if the entry already exists in Firebase
      const checkResponse = await fetch(
        `${
          import.meta.env.VITE_API_BASE_URL
        }/api/timing/check-participant?eventId=${encodeURIComponent(
          eventId
        )}&bib=${encodeURIComponent(bib)}`,
        {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        }
      );
      const checkText = await checkResponse.text();

      if (!checkResponse.ok) {
        let errorMessage = "Failed to check BIB";
        let errorStatus = "Error: Server error";
        if (checkResponse.status === 404) {
          errorMessage = `BIB ${bib} not found in participant list.`;
          errorStatus = "Error: BIB not found";
        } else {
          errorMessage = `Failed to check BIB ${bib}: Server error (status ${checkResponse.status}).`;
          errorStatus = `Error: Server error (status ${checkResponse.status})`;
        }
        setHistory((prev) => {
          const updatedHistory = prev.map((entry) =>
            entry.bib === bib &&
            entry.roundId === roundId &&
            entry.pendingId === pendingId
              ? {
                  ...entry,
                  status: errorStatus,
                  icon: "times-circle",
                }
              : entry
          );
          setPendingBibs((prevPending) => {
            const updatedPending = prevPending.filter(
              (entry) =>
                !(
                  entry.bib === bib &&
                  entry.roundId === roundId &&
                  entry.pendingId === pendingId
                )
            );
            saveToLocalStorage(updatedHistory, selectedRoundId, updatedPending);
            return updatedPending;
          });
          setMessage(<span>{errorMessage}</span>);
          return updatedHistory;
        });
        console.error(`[ERROR] handleOnlineSubmit: ${errorMessage}`, {
          bib,
          roundId,
        });
        return { success: false, error: errorStatus };
      }

      let participantData;
      try {
        participantData = JSON.parse(checkText);
      } catch (parseError) {
        const errorMessage = `Failed to parse participant data for BIB ${bib}.`;
        setHistory((prev) => {
          const updatedHistory = prev.map((entry) =>
            entry.bib === bib &&
            entry.roundId === roundId &&
            entry.pendingId === pendingId
              ? {
                  ...entry,
                  status: "Error: Invalid server response",
                  icon: "times-circle",
                }
              : entry
          );
          setPendingBibs((prevPending) => {
            const updatedPending = prevPending.filter(
              (entry) =>
                !(
                  entry.bib === bib &&
                  entry.roundId === roundId &&
                  entry.pendingId === pendingId
                )
            );
            saveToLocalStorage(updatedHistory, selectedRoundId, updatedPending);
            return updatedPending;
          });
          setMessage(<span>{errorMessage}</span>);
          return updatedHistory;
        });
        console.error(`[ERROR] handleOnlineSubmit: Parse error`, {
          bib,
          roundId,
          error: parseError.message,
        });
        return { success: false, error: "Parse error" };
      }

      if (!participantData || participantData.exists === false) {
        setHistory((prev) => {
          const updatedHistory = prev.map((entry) =>
            entry.bib === bib &&
            entry.roundId === roundId &&
            entry.pendingId === pendingId
              ? {
                  ...entry,
                  status: "Error: BIB not found",
                  icon: "times-circle",
                }
              : entry
          );
          setPendingBibs((prevPending) => {
            const updatedPending = prevPending.filter(
              (entry) =>
                !(
                  entry.bib === bib &&
                  entry.roundId === roundId &&
                  entry.pendingId === pendingId
                )
            );
            saveToLocalStorage(updatedHistory, selectedRoundId, updatedPending);
            return updatedPending;
          });
          setMessage(<span>BIB {bib} not found in participant list.</span>);
          return updatedHistory;
        });
        console.error(`[ERROR] handleOnlineSubmit: BIB not found`, {
          bib,
          roundId,
        });
        return { success: false, error: "BIB not found" };
      }

      // Check if the round already exists in Firebase
      if (
        participantData.completedRounds &&
        participantData.completedRounds.includes(roundId)
      ) {
        const nextRoundId = await getNextRoundId(bib);
        if (nextRoundId) {
          const nextRoundData = rounds.find((r) => r.id === nextRoundId);
          const newPendingId = uuidv4();
          setHistory((prev) => {
            const updatedHistory = prev.map((entry) =>
              entry.bib === bib &&
              entry.roundId === roundId &&
              entry.pendingId === pendingId
                ? {
                    ...entry,
                    roundId: nextRoundId,
                    location: nextRoundData?.name || nextRoundId,
                    status: "Tertunda",
                    icon: "exclamation-circle",
                    pendingId: newPendingId,
                  }
                : entry
            );
            setPendingBibs((prevPending) => {
              const updatedPending = prevPending.map((entry) =>
                entry.bib === bib &&
                entry.roundId === roundId &&
                entry.pendingId === pendingId
                  ? {
                      ...entry,
                      roundId: nextRoundId,
                      status: "Tertunda",
                      pendingId: newPendingId,
                    }
                  : entry
              );
              saveToLocalStorage(updatedHistory, nextRoundId, updatedPending);
              return updatedPending;
            });
            setMessage(
              <span>
                BIB {bib} already recorded in round {roundId}. Moved to round{" "}
                {nextRoundId} and syncing.
              </span>
            );
            console.log(
              `[INFO] handleOnlineSubmit: BIB ${bib} moved to round ${nextRoundId}, attempting sync`
            );
            // Immediately attempt to sync the moved entry
            handleOnlineSubmit(bib, eventId, nextRoundId, time, newPendingId);
            return updatedHistory;
          });
          return { success: false, error: "Moved to next round" };
        } else {
          setHistory((prev) => {
            const updatedHistory = prev.map((entry) =>
              entry.bib === bib &&
              entry.roundId === roundId &&
              entry.pendingId === pendingId
                ? {
                    ...entry,
                    status: `Sudah direkam sebelumnya di ronde ${roundId}`,
                    icon: "info-circle",
                  }
                : entry
            );
            setPendingBibs((prevPending) => {
              const updatedPending = prevPending.filter(
                (entry) =>
                  !(
                    entry.bib === bib &&
                    entry.roundId === roundId &&
                    entry.pendingId === pendingId
                  )
              );
              saveToLocalStorage(
                updatedHistory,
                selectedRoundId,
                updatedPending
              );
              return updatedPending;
            });
            setMessage(
              <span>
                Sync failed for BIB {bib}: Round {roundId} already recorded and
                no further rounds available.
              </span>
            );
            console.log(
              `[INFO] handleOnlineSubmit: No further rounds for BIB ${bib} in round ${roundId}`
            );
            return updatedHistory;
          });
          return { success: false, error: "Round already recorded" };
        }
      }

      // Get the correct round to submit based on server data
      const nextRoundId = await getNextRoundId(bib);
      const roundIdToSubmit = nextRoundId || roundId;
      const roundDataToSubmit = rounds.find((r) => r.id === roundIdToSubmit);
      if (!roundDataToSubmit) {
        const errorMessage = `Invalid round ${roundIdToSubmit} for BIB ${bib}.`;
        setHistory((prev) => {
          const updatedHistory = prev.map((entry) =>
            entry.bib === bib &&
            entry.roundId === roundId &&
            entry.pendingId === pendingId
              ? {
                  ...entry,
                  status: `Error: Invalid round`,
                  icon: "times-circle",
                }
              : entry
          );
          setPendingBibs((prevPending) => {
            const updatedPending = prevPending.filter(
              (entry) =>
                !(
                  entry.bib === bib &&
                  entry.roundId === roundId &&
                  entry.pendingId === pendingId
                )
            );
            saveToLocalStorage(updatedHistory, selectedRoundId, updatedPending);
            return updatedPending;
          });
          setMessage(<span>{errorMessage}</span>);
          return updatedHistory;
        });
        console.error(`[ERROR] handleOnlineSubmit: Invalid round`, {
          bib,
          roundId: roundIdToSubmit,
        });
        return { success: false, error: "Invalid round" };
      }

      const payload = {
        eventId,
        bib,
        roundId: roundIdToSubmit,
        timestamp: time,
      };
      console.log(
        `[DEBUG] handleOnlineSubmit: Sending payload to /record-time`,
        payload
      );
      const recordResponse = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/api/timing/record-time`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );

      const recordText = await recordResponse.text();
      if (recordResponse.ok) {
        let data;
        try {
          data = JSON.parse(recordText);
          // Check if the entry already exists in Firebase to avoid false errors
          if (!data.savedToFirebase) {
            const recheckResponse = await fetch(
              `${
                import.meta.env.VITE_API_BASE_URL
              }/api/timing/check-participant?eventId=${encodeURIComponent(
                eventId
              )}&bib=${encodeURIComponent(bib)}`,
              {
                method: "GET",
                headers: { "Content-Type": "application/json" },
              }
            );
            if (recheckResponse.ok) {
              const recheckData = await recheckResponse.json();
              if (
                recheckData.completedRounds &&
                recheckData.completedRounds.includes(roundIdToSubmit)
              ) {
                setHistory((prev) => {
                  const updatedHistory = prev.map((entry) =>
                    entry.bib === bib &&
                    entry.roundId === roundId &&
                    entry.pendingId === pendingId
                      ? {
                          ...entry,
                          roundId: roundIdToSubmit,
                          location: roundDataToSubmit.name || roundIdToSubmit,
                          status: "Berhasil disimpan",
                          icon: "check-circle",
                        }
                      : entry
                  );
                  setPendingBibs((prevPending) => {
                    const updatedPending = prevPending.filter(
                      (entry) =>
                        !(
                          entry.bib === bib &&
                          entry.roundId === roundId &&
                          entry.pendingId === pendingId
                        )
                    );
                    saveToLocalStorage(
                      updatedHistory,
                      selectedRoundId,
                      updatedPending
                    );
                    return updatedPending;
                  });
                  setMessage(<span>Sync successful for BIB {bib}</span>);
                  console.log(
                    `[INFO] handleOnlineSubmit: BIB ${bib} already exists in round ${roundIdToSubmit}, marked as successful`
                  );
                  return updatedHistory;
                });
                return { success: true };
              }
            }
            throw new Error("Server did not confirm Firebase save");
          }
        } catch (parseError) {
          const errorMessage = `Failed to parse sync response or confirm Firebase save for BIB ${bib}.`;
          setHistory((prev) => {
            const updatedHistory = prev.map((entry) =>
              entry.bib === bib &&
              entry.roundId === roundId &&
              entry.pendingId === pendingId
                ? {
                    ...entry,
                    status: "Error: Parse error or Firebase save failed",
                    icon: "times-circle",
                  }
                : entry
            );
            setPendingBibs((prevPending) => {
              const updatedPending = prevPending.filter(
                (entry) =>
                  !(
                    entry.bib === bib &&
                    entry.roundId === roundId &&
                    entry.pendingId === pendingId
                  )
              );
              saveToLocalStorage(
                updatedHistory,
                selectedRoundId,
                updatedPending
              );
              return updatedPending;
            });
            setMessage(<span>{errorMessage}</span>);
            return updatedHistory;
          });
          console.error(`[ERROR] handleOnlineSubmit: Parse error`, {
            bib,
            roundId,
            error: parseError.message,
          });
          return {
            success: false,
            error: "Parse error or Firebase save failed",
          };
        }
        setHistory((prev) => {
          const updatedHistory = prev.map((entry) =>
            entry.bib === bib &&
            entry.roundId === roundId &&
            entry.pendingId === pendingId
              ? {
                  ...entry,
                  roundId: roundIdToSubmit,
                  location:
                    data.message?.split("di ")[1] ||
                    roundDataToSubmit.name ||
                    roundIdToSubmit,
                  status: "Berhasil disimpan",
                  icon: "check-circle",
                }
              : entry
          );
          setPendingBibs((prevPending) => {
            const updatedPending = prevPending.filter(
              (entry) =>
                !(
                  entry.bib === bib &&
                  entry.roundId === roundId &&
                  entry.pendingId === pendingId
                )
            );
            saveToLocalStorage(updatedHistory, selectedRoundId, updatedPending);
            return updatedPending;
          });
          setMessage(<span>Sync successful for BIB {bib}</span>);
          console.log(
            `[INFO] handleOnlineSubmit: Sync successful for BIB ${bib} in round ${roundIdToSubmit}`
          );
          return updatedHistory;
        });
        return { success: true };
      } else {
        let errorData;
        try {
          errorData = JSON.parse(recordText);
        } catch (parseError) {
          const errorMessage = `Failed to parse error response for BIB ${bib}.`;
          setHistory((prev) => {
            const updatedHistory = prev.map((entry) =>
              entry.bib === bib &&
              entry.roundId === roundId &&
              entry.pendingId === pendingId
                ? {
                    ...entry,
                    status: "Error: Parse error",
                    icon: "times-circle",
                  }
                : entry
            );
            setPendingBibs((prevPending) => {
              const updatedPending = prevPending.filter(
                (entry) =>
                  !(
                    entry.bib === bib &&
                    entry.roundId === roundId &&
                    entry.pendingId === pendingId
                  )
              );
              saveToLocalStorage(
                updatedHistory,
                selectedRoundId,
                updatedPending
              );
              return updatedPending;
            });
            setMessage(<span>{errorMessage}</span>);
            return updatedHistory;
          });
          console.error(`[ERROR] handleOnlineSubmit: Parse error`, {
            bib,
            roundId,
            error: parseError.message,
          });
          return { success: false, error: "Parse error" };
        }
        if (
          errorData.message.includes("Round") &&
          errorData.message.includes("already recorded")
        ) {
          const nextRoundId = await getNextRoundId(bib);
          if (nextRoundId) {
            const nextRoundData = rounds.find((r) => r.id === nextRoundId);
            const newPendingId = uuidv4();
            setHistory((prev) => {
              const updatedHistory = prev.map((entry) =>
                entry.bib === bib &&
                entry.roundId === roundId &&
                entry.pendingId === pendingId
                  ? {
                      ...entry,
                      roundId: nextRoundId,
                      location: nextRoundData?.name || nextRoundId,
                      status: "Tertunda",
                      icon: "exclamation-circle",
                      pendingId: newPendingId,
                    }
                  : entry
              );
              setPendingBibs((prevPending) => {
                const updatedPending = prevPending.map((entry) =>
                  entry.bib === bib &&
                  entry.roundId === roundId &&
                  entry.pendingId === pendingId
                    ? {
                        ...entry,
                        roundId: nextRoundId,
                        status: "Tertunda",
                        pendingId: newPendingId,
                      }
                    : entry
                );
                saveToLocalStorage(updatedHistory, nextRoundId, updatedPending);
                return updatedPending;
              });
              setMessage(
                <span>
                  BIB {bib} already recorded in round {roundId}. Moved to round{" "}
                  {nextRoundId} and syncing.
                </span>
              );
              console.log(
                `[INFO] handleOnlineSubmit: BIB ${bib} moved to round ${nextRoundId}, attempting sync`
              );
              // Immediately attempt to sync the moved entry
              handleOnlineSubmit(bib, eventId, nextRoundId, time, newPendingId);
              return updatedHistory;
            });
            return { success: false, error: "Moved to next round" };
          } else {
            setHistory((prev) => {
              const updatedHistory = prev.map((entry) =>
                entry.bib === bib &&
                entry.roundId === roundId &&
                entry.pendingId === pendingId
                  ? {
                      ...entry,
                      status: `Sudah direkam sebelumnya di ronde ${roundId}`,
                      icon: "info-circle",
                    }
                  : entry
              );
              setPendingBibs((prevPending) => {
                const updatedPending = prevPending.filter(
                  (entry) =>
                    !(
                      entry.bib === bib &&
                      entry.roundId === roundId &&
                      entry.pendingId === pendingId
                    )
                );
                saveToLocalStorage(
                  updatedHistory,
                  selectedRoundId,
                  updatedPending
                );
                return updatedPending;
              });
              setMessage(
                <span>
                  Sync failed for BIB {bib}: Round {roundId} already recorded
                  and no further rounds available.
                </span>
              );
              console.log(
                `[INFO] handleOnlineSubmit: No further rounds for BIB ${bib} in round ${roundId}`
              );
              return updatedHistory;
            });
            return { success: false, error: "Round already recorded" };
          }
        } else {
          const errorMessage = errorData.message.includes("Invalid timestamp")
            ? `Sync failed for BIB ${bib} in ${roundId}: Round start time not reached.`
            : `Sync failed for BIB ${bib}: ${
                errorData.message ||
                `Server error (status ${recordResponse.status})`
              }`;
          setHistory((prev) => {
            const updatedHistory = prev.map((entry) =>
              entry.bib === bib &&
              entry.roundId === roundId &&
              entry.pendingId === pendingId
                ? {
                    ...entry,
                    status: `Error: ${
                      errorData.message ||
                      `Server error (status ${recordResponse.status})`
                    }`,
                    icon: "times-circle",
                  }
                : entry
            );
            setPendingBibs((prevPending) => {
              const updatedPending = prevPending.filter(
                (entry) =>
                  !(
                    entry.bib === bib &&
                    entry.roundId === roundId &&
                    entry.pendingId === pendingId
                  )
              );
              saveToLocalStorage(
                updatedHistory,
                selectedRoundId,
                updatedPending
              );
              return updatedPending;
            });
            setMessage(<span>{errorMessage}</span>);
            console.error(`[ERROR] handleOnlineSubmit: ${errorMessage}`, {
              bib,
              roundId,
            });
            return updatedHistory;
          });
          return { success: false, error: errorMessage };
        }
      }
    } catch (error) {
      const errorMessage = `Sync failed for BIB ${bib}: Network issue, data remains pending.`;
      setHistory((prev) => {
        saveToLocalStorage(prev, selectedRoundId, pendingBibs);
        setMessage(<span>{errorMessage}</span>);
        console.error(`[ERROR] handleOnlineSubmit: Network issue`, {
          bib,
          roundId,
          error: error.message,
        });
        return prev;
      });
      return { success: false, error: "Network issue" };
    }
  };

  const handleSyncClick = async () => {
    if (pendingBibs.length === 0) {
      setMessage(<span>No pending BIBs to synchronize.</span>);
      saveToLocalStorage(history, selectedRoundId, pendingBibs);
      return;
    }
    if (navigator.onLine) {
      setMessage(<span>Synchronizing pending data...</span>);
      console.log(
        `[DEBUG] handleSyncClick: Starting sync for ${pendingBibs.length} pending BIBs`,
        pendingBibs
      );
      let updatedHistory = [...history];
      let updatedPendingBibs = [...pendingBibs];

      const promises = pendingBibs.map((pendingBib) =>
        handleOnlineSubmit(
          pendingBib.bib,
          pendingBib.eventId,
          pendingBib.roundId,
          pendingBib.time,
          pendingBib.pendingId
        ).then((result) => ({
          pendingBib,
          result,
        }))
      );

      const results = await Promise.all(promises);

      results.forEach(({ pendingBib, result }) => {
        updatedHistory = updatedHistory.map((entry) =>
          entry.bib === pendingBib.bib &&
          entry.roundId === pendingBib.roundId &&
          entry.pendingId === pendingBib.pendingId
            ? {
                ...entry,
                status: result.success
                  ? "Berhasil disimpan"
                  : result.error.includes("Round already recorded")
                  ? `Sudah direkam sebelumnya di ronde ${pendingBib.roundId}`
                  : `Error: ${result.error || "Sync failed"}`,
                icon: result.success
                  ? "check-circle"
                  : result.error.includes("Round already recorded")
                  ? "info-circle"
                  : "times-circle",
              }
            : entry
        );

        if (result.success || result.error.includes("Round already recorded")) {
          updatedPendingBibs = updatedPendingBibs.filter(
            (entry) =>
              !(
                entry.bib === pendingBib.bib &&
                entry.roundId === pendingBib.roundId &&
                entry.pendingId === pendingBib.pendingId
              )
          );
        }
      });

      setHistory(updatedHistory);
      setPendingBibs(updatedPendingBibs);
      saveToLocalStorage(updatedHistory, selectedRoundId, updatedPendingBibs);
      setMessage(<span>Sync completed.</span>);
      console.log(`[INFO] handleSyncClick: Sync completed`, {
        remainingPending: updatedPendingBibs.length,
      });
    } else {
      setMessage(<span>You are offline. Please connect to synchronize.</span>);
      saveToLocalStorage(history, selectedRoundId, pendingBibs);
      console.warn(`[WARN] handleSyncClick: Device offline`);
    }
  };

  const handleClearHistoryClick = () => {
    if (clearHistoryWarning) {
      setHistory([]);
      setPendingBibs([]);
      setInputCount({});
      setSelectedRoundId("");
      localStorage.removeItem("history");
      localStorage.removeItem("pendingBibs");
      localStorage.removeItem("inputCount");
      localStorage.removeItem("bibNumber");
      localStorage.removeItem("message");
      localStorage.setItem("selectedRound", "");
      setClearHistoryWarning(false);
      if (clearHistoryTimeout) clearTimeout(clearHistoryTimeout);
      setMessage(<span>History cleared.</span>);
    } else {
      setClearHistoryWarning(true);
      setMessage(<span>Click again to clear!</span>);
      const timeout = setTimeout(() => {
        setClearHistoryWarning(false);
        setMessage("");
      }, 3000);
      setClearHistoryTimeout(timeout);
    }
  };

  const roundName =
    rounds.find((round) => round.id === selectedRoundId)?.name || "N/A";

  return (
    <div className="bg-yellow-50">
      <div className="text-center h-screen flex flex-col justify-between py-10 max-w-screen-sm mx-auto">
        <div
          className="mx-4 bg-white border border-yellow-400 rounded-2xl relative"
          style={{
            flex: 1,
            overflowY: "auto",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
          }}
        >
          <div className="grid grid-cols-4 gap-4 sticky top-0 bg-white text-yellow-600 pt-4 pb-1 z-10 text-sm font-semibold">
            <div>BIB</div>
            <div>Time</div>
            <div>{roundName}</div>
            <div>Status</div>
          </div>
          <div className="overflow-y-auto max-h-[300px] relative">
            {history.length === 0 ? (
              <div className="text-center text-sm py-8 text-yellow-600">
                No history available
              </div>
            ) : (
              history.map((entry) => (
                <div
                  key={entry.pendingId}
                  className="grid grid-cols-4 gap-4 mt-2 text-sm"
                >
                  <div>{entry.bib}</div>
                  <div>
                    {moment(entry.time).tz("Asia/Jakarta").format("HH:mm:ss")}
                  </div>
                  <div>
                    {entry.roundId ? entry.location || entry.roundId : "N/A"}
                  </div>
                  <div className="flex justify-center">
                    {entry.status === "Tertunda" && (
                      <FontAwesomeIcon
                        icon={faExclamationCircle}
                        className="text-yellow-500"
                      />
                    )}
                    {entry.status === "Berhasil disimpan" && (
                      <FontAwesomeIcon
                        icon={faCheckCircle}
                        className="text-green-500"
                      />
                    )}
                    {entry.status.includes("Error") && (
                      <FontAwesomeIcon
                        icon={faTimesCircle}
                        className="text-red-500"
                      />
                    )}
                    {(entry.status === "Input ganda terdeteksi" ||
                      entry.status.includes("Sudah direkam sebelumnya")) && (
                      <FontAwesomeIcon
                        icon={faInfoCircle}
                        className="text-yellow-600"
                      />
                    )}
                  </div>
                </div>
              ))
            )}
            <div ref={historyEndRef} />
          </div>
        </div>

        <input
          type="text"
          value={bibNumber}
          readOnly
          className="mx-4 text-2xl text-center py-4 mt-4 px-4 border border-yellow-400 focus:outline-none focus:ring-2 focus:ring-yellow-400 rounded-2xl"
        />
        <div className="mt-4 flex items-center justify-center gap-4">
          {message && (
            <p
              className={`font-normal text-sm ${
                (typeof message === "string"
                  ? message
                  : message?.props?.children?.toString() || ""
                ).includes("Error") ||
                (typeof message === "string"
                  ? message
                  : message?.props?.children?.toString() || ""
                ).includes("Failed") ||
                (typeof message === "string"
                  ? message
                  : message?.props?.children?.toString() || ""
                ).includes("offline") ||
                (typeof message === "string"
                  ? message
                  : message?.props?.children?.toString() || ""
                ).includes("not reached") ||
                (typeof message === "string"
                  ? message
                  : message?.props?.children?.toString() || ""
                ).includes("ended")
                  ? "text-red-500"
                  : "text-yellow-600"
              }`}
            >
              {message}
            </p>
          )}
        </div>
        <div className="w-full py-5">
          <div className="grid grid-cols-3 gap-2 px-4">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
              <button
                key={num}
                type="button"
                onClick={() => handleNumpadClick(num)}
                className="py-4 text-lg border border-yellow-400 rounded-2xl cursor-pointer transform transition-all duration-200 ease-in-out active:scale-108 active:shadow-md bg-white hover:bg-yellow-100 active:bg-yellow-400"
              >
                {num}
              </button>
            ))}
            <button
              type="button"
              onClick={handleSyncClick}
              className="py-4 text-lg border border-yellow-400 rounded-2xl cursor-pointer transform transition-all duration-200 ease-in-out active:scale-108 active:shadow-md bg-white hover:bg-yellow-100 active:bg-yellow-400"
            >
              Sync
            </button>
            <button
              type="button"
              onClick={() => handleNumpadClick(0)}
              className="py-4 text-lg border border-yellow-400 rounded-2xl cursor-pointer transform transition-all duration-200 ease-in-out active:scale-108 active:shadow-md bg-white hover:bg-yellow-100 active:bg-yellow-400"
            >
              0
            </button>
            <button
              type="button"
              onClick={handleDelete}
              className="py-4 text-lg border border-yellow-400 rounded-2xl cursor-pointer transform transition-all duration-200 ease-in-out active:scale-108 active:shadow-md bg-white hover:bg-yellow-100 active:bg-yellow-400"
            >
              Delete
            </button>
            <button
              type="button"
              onClick={handleClearHistoryClick}
              className="py-4 text-lg border border-yellow-400 rounded-2xl cursor-pointer transform transition-all duration-200 ease-in-out active:scale-108 active:shadow-md bg-rose-400 text-white hover:bg-rose-500 active:bg-rose-600"
            >
              <FontAwesomeIcon icon={faTrash} />
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              className="col-span-2 py-2 text-lg font-semibold border-none rounded-2xl cursor-pointer transform transition-all duration-200 ease-in-out active:scale-108 active:shadow-md bg-yellow-400 text-white hover:bg-yellow-500 active:bg-yellow-600"
            >
              Submit
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InputBIB;
